import { FeatureEntitlementName } from '@jupiterone/entitlement-types';
import {
  getFeatureEntitlementNameFromPathName,
  useDefaultAppSetting,
  useEntitlements,
  MockHook,
  useFeatureFlags,
  LANDING_PATH,
  AnalyticEventType,
  fireAnalyticEvent,
  useABAC,
} from '@jupiterone/jupiter-web-toolkit';
import { useLocation } from 'react-router-dom';
import { useEffect, useLayoutEffect, useState } from 'react';

export interface FeatureEntitlementStateProps {
  name?: FeatureEntitlementName;
  trialEndDate?: string;
}

export interface JupiterAppBarProps {
  collapsed?: boolean;
  children?: any;
}

const useJupiterAppBarLogic = ({
  children,
  collapsed = false,
}: JupiterAppBarProps) => {
  const { enforce } = useABAC();
  const hasAccessToLanding = enforce(['accessLanding']);
  const location = useLocation();
  let onLanding = false;

  useEffect(() => {
    if (location && location.pathname === LANDING_PATH) {
      onLanding = true;
    }
  }, [location.pathname]);

  const {
    loading: entitlementsLoading,
    isTrialFeatureEntitlement,
    getTrialEndDate,
  } = useEntitlements();
  const [defaultAppPath] = useDefaultAppSetting();
  const { hasFeature } = useFeatureFlags();
  const notificationsEnabled = hasFeature('notifications');
  const searchAnywhereEnabled = hasFeature('navSearchAnywhere');
  const [searchAnywhereOpen, setSearchAnywhereOpen] = useState(false);
  const computeFeatureEntitlementState = (): FeatureEntitlementStateProps => {
    const featureEntitlementName = getFeatureEntitlementNameFromPathName(
      location.pathname
    );
    let featureEntitlementTrialEndDate = undefined;

    if (featureEntitlementName) {
      const isTrial = isTrialFeatureEntitlement(featureEntitlementName);
      if (isTrial) {
        featureEntitlementTrialEndDate = getTrialEndDate(
          featureEntitlementName
        );
      }
    }
    return {
      name: featureEntitlementName,
      trialEndDate: featureEntitlementTrialEndDate,
    };
  };
  const [
    featureEntitlementState,
    setFeatureEntitlementState,
  ] = useState<FeatureEntitlementStateProps>(computeFeatureEntitlementState());
  useEffect(() => {
    setFeatureEntitlementState(computeFeatureEntitlementState());
  }, [location.pathname, entitlementsLoading]);

  const handleSearchAnywhereToggle = () =>
    setSearchAnywhereOpen(!searchAnywhereOpen);
  const handleSearchAnywhereDisable = () => setSearchAnywhereOpen(false);

  useLayoutEffect(() => {
    fireAnalyticEvent({
      type: AnalyticEventType.VISITOR_RESOLUTION,
      metadata: {
        browserWidth: window.innerWidth,
        browserHeight: window.innerHeight,
        browserResolution: `${window.innerWidth} x ${window.innerHeight}`,
        screenWidth: window.screen.width,
        screenHeight: window.screen.height,
        screenResolution: `${window.screen.width} x ${window.screen.height}`,
      },
    });
  }, []);

  return {
    searchAnywhereEnabled,
    searchAnywhereOpen: onLanding ? false : searchAnywhereOpen,
    notificationsEnabled,
    collapsed,
    defaultAppPath,
    featureEntitlementState,
    children,
    location,
    hasAccessToLanding,
    handleSearchAnywhereToggle,
    handleSearchAnywhereDisable,
  };
};

export type JupiterAppBarComponentProps = ReturnType<
  typeof useJupiterAppBarLogic
>;
export const MOCK_JUPITER_APP_BAR_PROPS: JupiterAppBarComponentProps = {
  notificationsEnabled: false,
  searchAnywhereEnabled: true,
  collapsed: false,
  defaultAppPath: '/',
  featureEntitlementState: {},
  children: null,
  location: { pathname: [] } as any,
  searchAnywhereOpen: false,
  hasAccessToLanding: true,
  handleSearchAnywhereToggle: MockHook.mockFunction,
  handleSearchAnywhereDisable: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  useJupiterAppBarLogic,
  MOCK_JUPITER_APP_BAR_PROPS
);
